import { useCallback, useEffect, useState } from "react";
import type { ProductDto, QuoteLineDto, QuoteRevisionDto } from "@grizzly/shared";
import { formatAmount } from "../../lib/format.js";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n } from "../../i18n/index.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { Trash2 } from "lucide-react";

interface AddForm {
  kind: "product" | "custom";
  productRevisionId: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

const EMPTY_ADD: AddForm = {
  kind: "custom",
  productRevisionId: "",
  description: "",
  quantity: "1",
  unitPrice: "",
};

export function QuoteLines({
  quoteId,
  revision,
  onChanged,
}: {
  quoteId: string;
  revision: QuoteRevisionDto | null;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<AddForm>(EMPTY_ADD);
  const [editing, setEditing] = useState<string | null>(null);
  const [editQty, setEditQty] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listProducts()
      .then((r) => setProducts(r.items))
      .catch(() => undefined);
  }, []);

  const isEditable = revision?.status === "draft";

  const submitAdd = useCallback(async () => {
    setBusy(true);
    try {
      await api.addQuoteLine(quoteId, revision!.id, {
        kind: form.kind,
        productRevisionId: form.productRevisionId || "",
        description: form.description,
        quantity: form.quantity,
        unitPrice: form.unitPrice,
      });
      push(t("sales.lineAdded"));
      setForm(EMPTY_ADD);
      setAddOpen(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [form, onChanged, push, quoteId, revision, t]);

  const startEdit = useCallback((line: QuoteLineDto) => {
    setEditing(line.id);
    setEditQty(line.quantity);
    setEditPrice((line.unitPriceMinor / 100).toFixed(2));
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editing || !revision) return;
    setBusy(true);
    try {
      await api.updateQuoteLine(quoteId, revision.id, editing, {
        quantity: editQty,
        unitPrice: editPrice,
      });
      push(t("sales.lineUpdated"));
      setEditing(null);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [editPrice, editQty, editing, onChanged, push, quoteId, revision, t]);

  const remove = useCallback(
    async (lineId: string) => {
      if (!revision) return;
      try {
        await api.deleteQuoteLine(quoteId, revision.id, lineId);
        push(t("sales.lineRemoved"));
        onChanged();
      } catch (err) {
        if (err instanceof ApiClientError) push(err.message, undefined, "danger");
      }
    },
    [onChanged, push, quoteId, revision, t]
  );

  const lines = revision?.lines ?? [];

  return (
    <section aria-label={t("sales.quoteLines")}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">{t("sales.lines")}</h2>
        {isEditable ? (
          <Button size="sm" variant="secondary" onClick={() => setAddOpen((v) => !v)}>
            {t("sales.addLine")}
          </Button>
        ) : null}
      </div>

      {lines.length === 0 && !addOpen ? (
        <p className="text-sm text-ink-muted">{t("sales.noLines")}</p>
      ) : (
        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded border border-line px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    {line.lineNo}. {line.description}
                    {line.kind === "product" ? (
                      <Badge tone="neutral">{t("sales.product")}</Badge>
                    ) : null}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {line.quantity} × {formatAmount(line.unitPriceMinor)}
                    {line.estimatedCostMinor > 0
                      ? ` · ${t("sales.estCost")} ${formatAmount(line.estimatedCostMinor)}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="tabular font-medium text-ink">
                    {formatAmount(line.lineTotalMinor)}
                  </span>
                  {isEditable ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(line)}>
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void remove(line.id)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
              {editing === line.id ? (
                <div className="mt-2 grid gap-2 border-t border-line pt-2 sm:grid-cols-3">
                  <TextInput
                    label={t("common.fields.quantity")}
                    value={editQty}
                    onChange={(e) => setEditQty(e.target.value)}
                  />
                  <TextInput
                    label={t("common.fields.unitPrice")}
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                  />
                  <div className="flex items-end gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={submitEdit}
                      disabled={busy}
                    >
                      {t("common.save")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {addOpen && revision ? (
        <div className="mt-3 space-y-3 rounded border border-line p-3">
          <SelectInput
            label={t("sales.lineType")}
            options={[
              { value: "custom", label: t("sales.customLine") },
              { value: "product", label: t("sales.fromProduct") },
            ]}
            value={form.kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, kind: e.target.value as "product" | "custom" }))
            }
          />
          {form.kind === "product" ? (
            <SelectInput
              label={t("sales.product")}
              options={[
                { value: "", label: t("sales.selectProduct") },
                ...products.map((p) => ({
                  value: p.latestRevisionId ?? "",
                  label: p.name,
                })),
              ].filter((o) => o.value)}
              value={form.productRevisionId}
              onChange={(e) =>
                setForm((f) => ({ ...f, productRevisionId: e.target.value }))
              }
            />
          ) : null}
          <TextInput
            label={t("common.fields.description")}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <TextInput
              label={t("common.fields.quantity")}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.unitPrice")}
              value={form.unitPrice}
              onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={submitAdd}
              disabled={
                busy ||
                !form.unitPrice ||
                !form.quantity ||
                (form.kind === "custom" && !form.description.trim())
              }
            >
              {busy ? t("sales.adding") : t("sales.saveLine")}
            </Button>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
